import {
  Component,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  SimpleChanges,
  OnInit,
  OnDestroy,
  HostListener,
  ElementRef
} from '@angular/core';
import { Channel } from 'stream-chat';
import { MatDialog } from '@angular/material/dialog';
import { ModalMoreactionComponent } from '../chat-modals/modal-moreaction/modal-moreaction.component';
import { ModalBlockuserComponent } from '../chat-modals/modal-blockuser/modal-blockuser.component';
import { Negotiation, Product, User, Shipping, ProductReservation, ChatChannel } from '@vestiaire/api-sdk-angular';
import { Store, select } from '@ngrx/store';
import { IAppState } from '@app/store';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { getCurrentCart } from '@app/core/cart/reducers';
import { LocaleService } from '@app/core/i18n/services/locale.service';
import { environment } from '@env/environment';

@Component({
  selector: 'vc-chat-header',
  templateUrl: './chat-header.component.html',
  styleUrls: ['./chat-header.component.scss']
})
export class ChatHeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() channel: Channel;
  @Input() userId: string;
  @Input() buyerId: string;
  @Input() sellerId: string;
  @Input() channelId: string;
  @Input() product: Product;
  @Input() shippingInformation: Shipping;
  @Input() negotiation: Negotiation;
  @Input() receiverInfo: User;
  @Input() isSeller: boolean;
  @Input() senderIsBlocked: boolean;
  @Input() receiverIsBlocked: boolean;
  @Input() isReserved: boolean;
  @Input() reservation: ProductReservation;
  @Input() email: string;
  @Input() chatFromPdp: boolean;

  @Output() blockReceiver: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() activeReportTracking: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() displayListMobile: EventEmitter<boolean> = new EventEmitter<boolean>();

  isOpenModal = false;
  isSellerOnVacation = false;
  isVipSeller = false;
  ngUnsubscribe = new Subject<void>();
  inCart = false;
  urlFormReport: string;
  pendingNegotiation = false;
  reportUserActivated: boolean;

  constructor(
    private dialogService: MatDialog,
    private store: Store<IAppState>,
    private localeService: LocaleService,
    private element: ElementRef
  ) {}

  ngOnInit() {
    this.store.pipe(select('mmao'), takeUntil(this.ngUnsubscribe)).subscribe(
      mmao => {
        if (mmao && mmao.negotiation && mmao.negotiation.id && mmao.negotiation.product.id === this.product.id) {
          this.negotiation = mmao.negotiation;
        }
      },
      _ => {}
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    const { product } = changes;

    this.store.pipe(select(getCurrentCart), takeUntil(this.ngUnsubscribe)).subscribe(
      res => {
        this.inCart = false;
        if (res && res.orderLines && Array.isArray(res.orderLines)) {
          this.inCart = res.orderLines.some(ol => this.product && ol.product.id === this.product.id);
        }
      },
      () => {}
    );

    if (product && product.currentValue) {
      this.isSellerOnVacation = product.currentValue.seller.vacation.active;
    }

    this.buyerId = this.isSeller && this.receiverInfo ? this.receiverInfo?.id : this.userId;
    this.isVipSeller = this.product?.seller?.segment === 'VIP';

    this.setUrlFormReport();

    this.pendingNegotiation = this.negotiation ? this.hasPendingNegotiations(this.negotiation) : false;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  @HostListener('document:click', ['$event'])
  handleClick(event: Event) {
    if (!this.element.nativeElement.contains(event.target)) {
      this.isOpenModal = false;
    }
  }

  setUrlFormReport() {
    const region = this.localeService.getLocale().getRegion();
    const language = this.localeService.getLocale().getLanguage();
    const lang = language === 'en' ? (language + '-' + region).toLowerCase() : language;
    const complained = this.buyerId === this.userId ? this.sellerId : this.buyerId;
    const country = this.localeService.getCountry().toLowerCase();
    const hash = this.email;
    const hashTranscripted = window.btoa(unescape(encodeURIComponent(hash)));
    const channelId = this.channelId;

    this.urlFormReport =
      'https://faq.vestiairecollective.com/hc/' +
      lang +
      '/requests/new?ticket_form_id=360000167277&n=3&item=' +
      this.product?.id +
      '&channel=' +
      channelId +
      '&complainer=' +
      this.userId +
      '&complained=' +
      complained +
      '&country=' +
      country +
      '&hash=' +
      hashTranscripted +
      '#top_form';
  }

  // open modal only for mobile not desktop
  moreActions() {
    const dialogRef = this.dialogService.open(ModalMoreactionComponent, {
      height: '45vh',
      maxWidth: 600,
      position: { bottom: '0', left: '0', right: '0' },
      data: {
        urlFormReport: this.urlFormReport,
        receiver: this.receiverInfo,
        receiverIsBlocked: this.receiverIsBlocked,
        channel: this.channel,
        reportUserActivated: this.reportUserActivated
      }
    });

    dialogRef.afterClosed().subscribe(
      res => {
        if (res.reportUserActivated) {
          this.trackReportClick(); // tracking if report-user action
        } else {
          this.blockReceiver.emit(res.receiverIsBlocked); // else block-user action
        }
      },
      err => {
        // Silently fails
      }
    );
  }

  blockUserActions() {
    this.isOpenModal = false;
    const dialogRef = this.dialogService.open(ModalBlockuserComponent, {
      height: '235px',
      width: '480px',
      position: {},
      data: {
        receiver: this.receiverInfo,
        receiverIsBlocked: this.receiverIsBlocked,
        channel: this.channel
      }
    });

    dialogRef.afterClosed().subscribe(
      res => this.blockReceiver.emit(res.receiverIsBlocked),
      err => {
        // Silently fails
      }
    );
  }

  trackReportClick() {
    this.activeReportTracking.emit();
  }

  hasPendingNegotiations(negotiation: Negotiation): boolean {
    const expirationDate = new Date(negotiation.expirationDate);

    return !negotiation.isClosed && expirationDate > new Date();
  }

  /**
   * Hide MMAO CTA :
   * - Buyer : cf. hideBuyerCTA + product is in cart
   * - Seller: cf. hideSellerMmaoCTA
   */
  get hideMmaoCTA(): boolean {
    const isInCart = !this.isSeller && this.inCart;

    return this.hideBuyerCTA || isInCart || this.hideSellerMmaoCTA;
  }

  /**
   * - Hide MMAO CTA for Seller:
   *    - seller is VIP
   *    - product sold
   *    - seller on vacation
   *    - product is reserved by another buyer
   */
  get hideSellerMmaoCTA(): boolean {
    const sellerInitiateOfferCase =
      (this.isVipSeller && !this.negotiation) ||
      (this.isReservedByPremium && !this.isReservedByReceiverInfo) ||
      this.productSold ||
      this.isSellerOnVacation;

    return this.isSeller && sellerInitiateOfferCase;
  }

  /**
   * Disable MMAO CTA :
   * - Seller:
   *    - prohibited product
   * - Both Buyer and Seller:
   *    - non negotiable product
   */
  get disabledMmaoCTA(): boolean {
    return !this.product?.negotiable || (this.isSeller && this.isProhibited);
  }

  /**
   * Hide Buy/MMAO CTA for buyer:
   * - isSeller
   * - Product is sold
   * - Seller on vacation
   * - Product is prohibited
   * - Reserved by prenium
   */
  get hideBuyNowCTA(): boolean {
    return this.isSeller || this.hideBuyerCTA;
  }

  get hideBuyerCTA(): boolean {
    const isSellerOnVacation = !this.isSeller && this.isSellerOnVacation;
    const isProhibited = !this.isSeller && this.isProhibited;
    const isReserved = !this.isSeller && this.isReservedByPremium;

    return this.productSold || isSellerOnVacation || isProhibited || isReserved;
  }

  /**
   * GENERIC CASE
   */
  get productSold(): boolean {
    return this.product && this.product.sold;
  }

  // buyer prohibited
  get isProhibited(): boolean {
    return this.shippingInformation && this.shippingInformation.prohibited;
  }

  // is the product already reserved by someone ?
  get isReservedByPremium(): boolean {
    return this.isReserved && !this.reservation?.reservedToMe;
  }

  // is the product reserved by the receiverInfo-buyer ?
  get isReservedByReceiverInfo(): boolean {
    return this.reservation?.reservedBy === this.receiverInfo?.id;
  }
}
